(() => {
  if (customElements.get('hh-product-gallery')) return;

  class HhProductGallery extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.connection = (this.connection || 0) + 1;
      const connection = this.connection;
      customElements.whenDefined('clever-slider').then(() => {
        if (this.isConnected && this.connection === connection) this.initialize(connection);
      });
    }

    initialize(connection, attempts = 0) {
      const slider = this.querySelector('clever-slider');
      if (!slider?.embla) {
        if (attempts < 120) {
          this.frame = requestAnimationFrame(() => {
            if (this.isConnected && this.connection === connection) this.initialize(connection, attempts + 1);
          });
        }
        return;
      }

      this.slider = slider;
      this.embla = slider.embla;
      this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-thumbnail]'));
      this.thumbnailStrip = this.querySelector('.hh-gallery__thumbnails');
      this.previous = this.querySelector('[data-gallery-previous]');
      this.next = this.querySelector('[data-gallery-next]');
      this.zoomQuery = matchMedia(`(hover: hover) and (pointer: fine) and (min-width: ${Number(this.dataset.mobileBreakpoint || 768) + 1}px)`);
      const options = { signal: this.abort.signal };

      this.previous?.addEventListener('click', () => this.embla.scrollPrev(), options);
      this.next?.addEventListener('click', () => this.embla.scrollNext(), options);
      this.thumbnails.forEach((button, index) => {
        button.disabled = false;
        button.addEventListener('click', () => this.embla.scrollTo(index), options);
        button.addEventListener('keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = (index + 1) % this.thumbnails.length;
          if (event.key === 'ArrowLeft') target = (index - 1 + this.thumbnails.length) % this.thumbnails.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = this.thumbnails.length - 1;
          if (target === undefined) return;
          event.preventDefault();
          this.embla.scrollTo(target);
          this.thumbnails[target].focus({ preventScroll: true });
        }, options);
      });

      this.querySelectorAll('[data-zoom-trigger]').forEach(wrapper => {
        wrapper.addEventListener('pointerenter', event => this.zoomImage(event, wrapper), options);
        wrapper.addEventListener('pointermove', event => this.zoomImage(event, wrapper), options);
        wrapper.addEventListener('pointerleave', () => this.resetZoom(), options);
      });
      this.zoomQuery.addEventListener('change', () => this.resetZoom(), options);

      this.onSelect = () => {
        this.resetZoom();
        this.sync();
        this.pauseInactiveMedia();
      };
      this.onReInit = () => {
        this.resetZoom();
        this.sync();
      };
      this.onPointerDown = () => {
        this.dragging = true;
        this.resetZoom();
      };
      this.onPointerUp = () => { this.dragging = false; };
      this.embla.on('select', this.onSelect);
      this.embla.on('reInit', this.onReInit);
      this.embla.on('pointerDown', this.onPointerDown);
      this.embla.on('pointerUp', this.onPointerUp);
      this.sync();
      this.pauseInactiveMedia();
    }

    sync() {
      const index = this.embla.selectedScrollSnap();
      this.thumbnails.forEach((button, thumbnailIndex) => {
        button.setAttribute('aria-pressed', String(thumbnailIndex === index));
      });
      this.embla.slideNodes().forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', String(slideIndex !== index));
        slide.inert = slideIndex !== index;
      });
      if (this.previous) this.previous.disabled = !this.embla.canScrollPrev();
      if (this.next) this.next.disabled = !this.embla.canScrollNext();

      const selected = this.thumbnails[index];
      const strip = this.thumbnailStrip;
      if (selected && strip) {
        const thumbnailBounds = selected.getBoundingClientRect();
        const stripBounds = strip.getBoundingClientRect();
        if (thumbnailBounds.left < stripBounds.left + 3) strip.scrollLeft -= stripBounds.left + 3 - thumbnailBounds.left;
        else if (thumbnailBounds.right > stripBounds.right - 3) strip.scrollLeft += thumbnailBounds.right - stripBounds.right + 3;
      }
    }

    pauseInactiveMedia() {
      const selected = this.embla.selectedScrollSnap();
      this.embla.slideNodes().forEach((slide, index) => {
        if (index === selected) return;
        slide.querySelectorAll('video').forEach(video => video.pause());
        slide.querySelectorAll('external-video').forEach(video => video.player?.pauseVideo?.());
        slide.querySelectorAll('model-viewer').forEach(model => model.pause?.());
      });
    }

    zoomImage(event, wrapper) {
      if (!this.zoomQuery.matches || this.dragging || event.pointerType === 'touch') return;
      const slide = wrapper.closest('cs-slide');
      if (slide?.getAttribute('aria-hidden') === 'true') return;
      const picture = wrapper.querySelector('picture');
      if (!picture) return;
      const bounds = wrapper.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      if (this.zoom?.wrapper !== wrapper) {
        this.resetZoom();
        const sources = Array.from(picture.querySelectorAll('source, img'));
        this.zoom = { wrapper, picture, transform: picture.style.transform, sources: sources.map(source => [source, source.getAttribute('sizes')]) };
        const sourceWidth = Number(this.dataset.zoomWidth) || 1600;
        sources.forEach(source => source.setAttribute('sizes', `${sourceWidth}px`));
      }
      const scale = Math.max(1, Math.min(3, (Number(this.dataset.zoomWidth) || 1600) / bounds.width));
      const x = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
      const y = Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height));
      picture.style.transform = `translate(${(0.5 - x) * (scale - 1) * 100}%, ${(0.5 - y) * (scale - 1) * 100}%) scale(${scale})`;
    }

    resetZoom() {
      if (!this.zoom) return;
      this.zoom.picture.style.transform = this.zoom.transform;
      this.zoom.sources.forEach(([source, sizes]) => {
        if (sizes === null) source.removeAttribute('sizes');
        else source.setAttribute('sizes', sizes);
      });
      this.zoom = null;
    }

    disconnectedCallback() {
      this.connection = (this.connection || 0) + 1;
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.resetZoom();
      if (!this.embla) return;
      this.embla.off('select', this.onSelect);
      this.embla.off('reInit', this.onReInit);
      this.embla.off('pointerDown', this.onPointerDown);
      this.embla.off('pointerUp', this.onPointerUp);
      this.embla = null;
    }
  }

  customElements.define('hh-product-gallery', HhProductGallery);
})();
